type QuillOp = {
  insert: string | Record<string, any>;
  attributes?: Record<string, any>;
};

export type ExportInlineRun = {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
  color?: string;
  background?: string;
  link?: string;
  script?: "sub" | "super";
  fontSizePt?: number;
};

export type ExportBlock = {
  type: "paragraph" | "page-break" | "image";
  runs?: ExportInlineRun[];
  leadingFirstLineIndentWidth?: string;
  align?: "left" | "center" | "right" | "justify";
  lineHeight?: number;
  indentLevel?: number;
  leftIndentWidth?: string;
  hasHangingIndent?: boolean;
  listType?: "ordered" | "bullet";
  headerLevel?: number;
  isBlockquote?: boolean;
  isCodeBlock?: boolean;
  firstLineIndentWidth?: string;
  imageSrc?: string;
};

const normalizeAlign = (align: any): ExportBlock["align"] => {
  switch (String(align ?? "").toLowerCase()) {
    case "center":
      return "center";
    case "right":
      return "right";
    case "justify":
      return "justify";
    default:
      return "left";
  }
};

const parseFontSizePt = (fontSize: any): number | undefined => {
  if (fontSize == undefined || fontSize == null || fontSize === "") {
    return undefined;
  }

  if (typeof fontSize === "number" && Number.isFinite(fontSize)) {
    return fontSize;
  }

  const raw = String(fontSize).trim().toLowerCase();
  const numeric = Number(raw.replace("pt", ""));
  if (!Number.isFinite(numeric)) return undefined;
  return numeric;
};

const sanitizeText = (value: string) => {
  return value.replace(/[\u200B-\u200D\uFEFF]/g, "");
};

const buildInlineRun = (
  text: string,
  attrs?: Record<string, any>
): ExportInlineRun => {
  return {
    text: sanitizeText(text),
    bold: attrs?.bold || false,
    italic: attrs?.italic || false,
    underline: attrs?.underline || false,
    strike: attrs?.strike || false,
    color: attrs?.color,
    background: attrs?.background,
    link: attrs?.link,
    script:
      attrs?.script === "sub" || attrs?.script === "super"
        ? attrs.script
        : undefined,
    fontSizePt: parseFontSizePt(attrs?.fontSize ?? attrs?.size),
  };
};

const pushTextChunk = (
  runs: ExportInlineRun[],
  text: string,
  attrs?: Record<string, any>
) => {
  if (!text) return;
  const run = buildInlineRun(text, attrs);
  if (!run.text) return;
  runs.push(run);
};

const extractLeadingTabs = (runs: ExportInlineRun[]) => {
  const normalizedRuns = runs.map((run) => ({ ...run }));
  let leadingTabCount = 0;
  let stillScanningLeadingContent = true;
  const cleanedRuns: ExportInlineRun[] = [];

  for (const run of normalizedRuns) {
    if (!stillScanningLeadingContent) {
      cleanedRuns.push(run);
      continue;
    }

    let text = run.text;
    let tabCountForRun = 0;

    while (text.startsWith("\t")) {
      tabCountForRun += 1;
      text = text.slice(1);
    }

    leadingTabCount += tabCountForRun;

    if (text.length > 0) {
      cleanedRuns.push({
        ...run,
        text,
      });
      stillScanningLeadingContent = false;
      continue;
    }

    if (tabCountForRun === 0) {
      cleanedRuns.push(run);
      stillScanningLeadingContent = false;
    }
  }

  return {
    leadingTabCount,
    runs: cleanedRuns,
  };
};

const toLeadingFirstLineIndentWidth = (leadingTabCount: number) => {
  if (!Number.isFinite(leadingTabCount) || leadingTabCount <= 0) {
    return undefined;
  }

  // Match the DOCX import path, which treats one leading tab as ~720 twips.
  const pointsPerTab = 720 / 20;
  return `${leadingTabCount * pointsPerTab}pt`;
};

export const useQuillOpsToExportBlocks = (opsInput: any): ExportBlock[] => {
  const ops: QuillOp[] = Array.isArray(opsInput)
    ? opsInput
    : Array.isArray(opsInput?.ops)
    ? opsInput.ops
    : [];

  const blocks: ExportBlock[] = [];
  let lineRuns: ExportInlineRun[] = [];
  let pendingFirstLineIndentWidth: string | undefined;

  const flushLine = (attrs?: Record<string, any>) => {
    const normalizedLine = extractLeadingTabs(lineRuns);

    blocks.push({
      type: "paragraph",
      runs: normalizedLine.runs,
      leadingFirstLineIndentWidth: toLeadingFirstLineIndentWidth(
        normalizedLine.leadingTabCount
      ),
      align: normalizeAlign(attrs?.align),
      lineHeight: Number(attrs?.lineheight ?? 1),
      indentLevel: Number(attrs?.indent ?? 0),
      leftIndentWidth: attrs?.leftIndent,
      hasHangingIndent: attrs?.hangingIndent != undefined,
      listType:
        attrs?.list === "ordered" || attrs?.list === "bullet"
          ? attrs.list
          : undefined,
      headerLevel:
        attrs?.header != undefined ? Number(attrs.header ?? 0) : undefined,
      isBlockquote: attrs?.blockquote === true,
      isCodeBlock: attrs?.["code-block"] === true,
      firstLineIndentWidth: attrs?.firstline ?? pendingFirstLineIndentWidth,
    });

    lineRuns = [];
    pendingFirstLineIndentWidth = undefined;
  };

  for (const op of ops) {
    if (typeof op.insert === "string") {
      const text = op.insert;
      const parts = text.split("\n");
      for (let i = 0; i < parts.length; i++) {
        pushTextChunk(lineRuns, parts[i], op.attributes);
        if (i < parts.length - 1) {
          flushLine(op.attributes);
        }
      }
      continue;
    }

    if (op.insert && typeof op.insert === "object") {
      if ("firstline-indent" in op.insert) {
        const value = (op.insert as any)["firstline-indent"];
        if (lineRuns.length === 0 && pendingFirstLineIndentWidth == undefined) {
          pendingFirstLineIndentWidth =
            typeof value === "string" ? value : value?.width ?? "2em";
        } else {
          // A second spacer within a line separates a literal Word list label
          // from its text. Keep that separation when exporting the edited file.
          pushTextChunk(lineRuns, "\t");
        }
        continue;
      }

      if ("pageBreak" in op.insert) {
        if (lineRuns.length > 0) {
          flushLine(op.attributes);
        }
        blocks.push({ type: "page-break" });
        continue;
      }

      if ("image" in op.insert && (op.insert as any).image) {
        if (lineRuns.length > 0) {
          flushLine(op.attributes);
        }
        blocks.push({
          type: "image",
          imageSrc: String((op.insert as any).image),
          align: normalizeAlign(op.attributes?.align),
        });
      }
    }
  }

  if (lineRuns.length > 0 || pendingFirstLineIndentWidth != undefined) {
    flushLine({});
  }

  return blocks;
};
